import { Card, Tag, Tooltip } from 'antd';
import { InfoCircleOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { App } from '../../types';
import { Typography } from 'antd';

const { Paragraph, Text } = Typography;

/** Default color palette for auto-assigning group colors */
const DEFAULT_PALETTE = [
  '#1e3a8a', '#0e7490', '#065f46', '#92400e',
  '#7c3aed', '#be185d', '#b45309', '#4338ca',
];

/** Generate initials from app name (always 3 letters, letters only) */
function getInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.replace(/[^a-zA-Z]/g, ''))
    .filter((w) => w.length > 0);
  if (words.length === 0) return '?';
  if (words.length === 1) {
    return words[0].substring(0, 3).toUpperCase();
  }
  if (words.length === 2) {
    return (words[0][0] + words[1][0] + (words[1][1] ?? '')).toUpperCase();
  }
  return words
    .slice(0, 3)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

export function normalizeHexColor(value?: string | null): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!match) return null;
  const raw = match[1];
  const hex = raw.length === 3
    ? raw.split('').map((char) => `${char}${char}`).join('')
    : raw;
  return `#${hex.toLowerCase()}`;
}

function getRgb(hex: string) {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

/** Lighten a hex color to create a gradient end */
export function lightenColor(hex: string): string {
  const { r, g, b } = getRgb(hex);
  const lighten = (c: number) => Math.min(255, c + Math.round((255 - c) * 0.3));
  return `#${lighten(r).toString(16).padStart(2, '0')}${lighten(g).toString(16).padStart(2, '0')}${lighten(b).toString(16).padStart(2, '0')}`;
}

export function getTextColorForBackground(hex: string): string {
  const { r, g, b } = getRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.68 ? '#1f2933' : 'white';
}

/** Pick a stable color from palette based on string hash */
export function getAutoColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  return DEFAULT_PALETTE[Math.abs(hash) % DEFAULT_PALETTE.length];
}

const subtleTooltip = { color: 'rgba(0,0,0,0.65)' } as const;

function formatTrialEnds(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Trial ends ${date.toLocaleDateString()}`;
}

function formatTrialEnded(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `Trial ended ${date.toLocaleDateString()}`;
}

type AppCardProps = {
  app: App;
  actions?: ReactNode[];
  onCardClick?: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  /** Override icon color (e.g. inherited from parent group) */
  iconColor?: string;
};

export default function AppCard({ app, actions, onCardClick, isFavorite, onToggleFavorite, iconColor }: AppCardProps) {
  const tags: { text: string; color: string }[] = [];
  const showSubscriptionTiming = !app.parentId || app.licenseAppId === app.id;

  if (app.isFree) {
    tags.push({ text: 'FREE', color: 'cyan' });
  }
  if (app.isAnneaOnly) {
    tags.push({ text: 'ANNEA', color: 'gold' });
  }
  if (app.isSubscribed && !app.isFree) {
    if (app.isTrial) {
      tags.push({ text: 'TRIAL', color: 'blue' });
    } else {
      tags.push({ text: 'SUBSCRIBED', color: 'green' });
    }
  } else if (app.hasOrgSubscription && !app.isFree) {
    tags.push({ text: 'ORG SUB', color: 'purple' });
  }
  if (!app.isSubscribed && app.trialEndedAt && !app.isFree) {
    tags.push({ text: 'TRIAL ENDED', color: 'red' });
  }
  if (app.badge) {
    const badgeColor = app.badge === 'NEW' ? 'blue' : app.badge === 'BETA' ? 'orange' : 'green';
    tags.push({ text: app.badge, color: badgeColor });
  }

  const isClickable = Boolean(onCardClick);
  const baseColor = normalizeHexColor(iconColor || app.iconColor) || getAutoColor(app.id);
  const initials = getInitials(app.name);
  const initialsColor = getTextColorForBackground(baseColor);
  const trialEndsText = showSubscriptionTiming && app.isTrial ? formatTrialEnds(app.trialEndsAt) : null;
  const trialEndedText = showSubscriptionTiming && !app.isTrial ? formatTrialEnded(app.trialEndedAt) : null;

  const body = (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
      {/* Left column: icon + action icons stacked vertically */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, minWidth: 44 }}>
        <div
          style={{
            width: 44,
            height: 44,
            minWidth: 44,
            borderRadius: 10,
            background: `linear-gradient(135deg, ${baseColor}, ${lightenColor(baseColor)})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: initialsColor,
            fontSize: initials.length > 2 ? 14 : 16,
            fontWeight: 700,
            letterSpacing: 0.5,
            userSelect: 'none',
          }}
        >
          {initials}
        </div>
        {/* Info and favorite icons below the app icon */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {onToggleFavorite && (
            <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'} {...subtleTooltip}>
              <div
                className="favorite-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite();
                }}
                style={{
                  cursor: 'pointer',
                  fontSize: 16,
                  color: isFavorite ? '#faad14' : '#d9d9d9',
                  opacity: isFavorite ? 1 : undefined,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {isFavorite ? <StarFilled /> : <StarOutlined />}
              </div>
            </Tooltip>
          )}
          {app.infoUrl && (
            <Tooltip title="More info" {...subtleTooltip}>
              <InfoCircleOutlined
                className="info-btn-inline"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(app.infoUrl!, '_blank', 'noopener,noreferrer');
                }}
                style={{ color: '#bfbfbf', fontSize: 15, cursor: 'pointer', opacity: 0, transition: 'opacity 0.2s ease' }}
              />
            </Tooltip>
          )}
        </div>
      </div>
      {/* Right column: title, tags, description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text
          strong
          ellipsis
          style={{ fontSize: 15, lineHeight: '22px', display: 'block' }}
        >
          {app.name}
        </Text>
        {tags.length > 0 && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
            {tags.map((tag) => (
              <Tag
                key={tag.text}
                color={tag.color}
                style={{ margin: 0, fontSize: 10, lineHeight: '18px', padding: '0 6px' }}
              >
                {tag.text}
              </Tag>
            ))}
          </div>
        )}
        {trialEndsText && (
          <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            {trialEndsText}
          </Text>
        )}
        {trialEndedText && (
          <Text type="danger" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
            {trialEndedText}
          </Text>
        )}
        <Paragraph
          ellipsis={{ rows: 5 }}
          style={{ marginTop: 6, marginBottom: 0, color: '#8c8c8c', fontSize: 13, lineHeight: '20px' }}
        >
          {app.description}
        </Paragraph>
      </div>
    </div>
  );

  return (
    <Card
      className={`app-card${isClickable ? ' clickable' : ''}`}
      onClick={onCardClick}
      actions={actions && actions.length > 0 ? actions : undefined}
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        cursor: isClickable ? 'pointer' : 'default',
        height: '100%',
      }}
      styles={{
        body: { padding: '20px 20px 16px' },
      }}
    >
      {isClickable ? (
        <Tooltip title={`Open ${app.name}`} mouseEnterDelay={0.4} {...subtleTooltip}>
          <div>{body}</div>
        </Tooltip>
      ) : (
        body
      )}
    </Card>
  );
}
